import { Avatar } from "@shared/ui/Avatar";
import { Type } from "@shared/ui/Type";
import GenericUploadInput from "./GenericUploadInput";
import { FieldBaseProps } from "@shared/features/form/useFieldProps";

const FILE_UPLOADER_HASH = "profile-upload";
const MAX_FILE_SIZE = 20 * 1024 * 1024;

export type Props = FieldBaseProps & {
  name: string;
};

async function resizeImage(
  file: File,
  maxWidth = 1200,
  maxHeight = 1200
): Promise<File> {
  if (!file.type.startsWith("image/") || file.size <= MAX_FILE_SIZE)
    return file;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(maxWidth / width, maxHeight / height, 1);
        width *= scale;
        height *= scale;

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Canvas context unavailable"));
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob)
              return reject(new Error("Failed to convert canvas to blob"));
            resolve(
              new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              })
            );
          },
          file.type,
          0.85
        );
      };
      img.onerror = () => reject(new Error("Image load failed"));
    };
    reader.onerror = () => reject(new Error("File read failed"));
  });
}

export function PhotoUploadInput({
  name,
  required,
  error,
  isSubmitting,
  className,
}: Props) {
  return (
    <GenericUploadInput
      name={name}
      hash={FILE_UPLOADER_HASH}
      maxFileSize={MAX_FILE_SIZE}
      required={required}
      error={error}
      isSubmitting={isSubmitting}
      className={className}
      allowedFileTypes={{
        "image/jpeg": true,
        "image/png": true,
        "image/gif": true,
        "image/svg+xml": true,
      }}
      resize={resizeImage}
      transform={(resized, base64) => ({
        file: base64,
        name: resized.name,
        size: resized.size,
        type: resized.type,
        original: resized,
      })}
      preview={(value) => <Avatar image={value.file} className="border" />}
      renderDetails={(value) => (
        <>
          <Type variant="h6" className="text-brand-700">
            {value.name}
          </Type>
        </>
      )}
      changeButtonLabel="Change profile picture"
    />
  );
}

export default PhotoUploadInput;
